import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Stack,
  Tooltip,
  Autocomplete,
  FormControlLabel,
  Radio,
  RadioGroup,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  ViewWeek as ViewWeekIcon,
  CalendarMonth as ViewMonthIcon,
  CalendarViewMonth as ViewYearIcon,
} from '@mui/icons-material';
import { PlanningEntry, PlanningCreate, PlanningUpdate, PlanningStatus, WeekPlanningResponse } from '../../../types/planning';
import { MachineDropdownItem, MachineConflictEntry, MachineStatus, MACHINE_STATUS_LABELS } from '../../../types/machine';
import { Projet } from '../../../types/projet';
import { Service } from '../../../types/service';
import { planningAPI, ateliersAPI, projetsAPI, servicesAPI, userAPI, machineAPI } from '../../../services/api';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import { extractErrorMessage } from '../../../utils/errorHandler';
import usePdfPreview from '../../../hooks/usePdfPreview';
import { useBEEtat } from '../../../hooks/queries/useBureauEtudes';
// generatePlanningPDF is dynamically imported in the print handlers so the
// jspdf chunk only loads when the user requests a planning PDF.

interface User {
  id: number;
  email: string;
  full_name: string;
  first_name?: string;
  last_name?: string;
}

type ViewMode = 'week' | 'month' | 'year';

// Time slots for the calendar (08:00 to 08:00 next day - 24 hours)
const TIME_SLOTS = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00',
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00',
  '20:00', '21:00', '22:00', '23:00', '00:00', '01:00',
  '02:00', '03:00', '04:00', '05:00', '06:00', '07:00', '08:00'
];

const DAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const DAYS_FR_SHORT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const STATUS_COLORS: Record<PlanningStatus, { bg: string; text: string; label: string }> = {
  en_cours: { bg: '#2196F3', text: '#fff', label: 'En cours' },
  termine: { bg: '#4CAF50', text: '#fff', label: 'Terminé' },
  bloque: { bg: '#f44336', text: '#fff', label: 'Bloqué' },
};

// Curated palette of Material-600/700 shades — visually distinct, all readable
// with white text. Each project/service code is hashed to a stable index so
// the same code always picks the same color across reloads and views.
// Red shades are intentionally excluded — the "bloqué" status uses red
// (#f44336) on the chip's inset left bar, so a red chip background would
// blur the status signal.
const PROJECT_PALETTE = [
  '#1976D2', // blue 700
  '#43A047', // green 600
  '#F57C00', // orange 700
  '#8E24AA', // purple 600
  '#00897B', // teal 600
  '#D81B60', // pink 600
  '#3949AB', // indigo 600
  '#6D4C41', // brown 600
  '#0097A7', // cyan 700
  '#5E35B1', // deep purple 600
  '#FFA000', // amber 700
  '#0288D1', // light blue 700
  '#558B2F', // light green 800
  '#00695C', // dark teal 800
  '#7B1FA2', // purple 700
  '#2E7D32', // green 800
  '#EF6C00', // orange 800
  '#AD1457', // pink 800
  '#1565C0', // blue 800
  '#827717', // olive 900
  '#4E342E', // brown 800
  '#283593', // indigo 800
];

const projectColor = (code: string | null | undefined): string => {
  const s = code ?? '';
  if (!s) return STATUS_COLORS.en_cours.bg;
  // FNV-1a hash → stable, well-distributed index even for neighbouring codes.
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return PROJECT_PALETTE[(h >>> 0) % PROJECT_PALETTE.length];
};

const MACHINE_STATUS_COLORS: Record<MachineStatus, { bg: string; text: string }> = {
  en_service: { bg: '#4CAF50', text: '#fff' },
  hors_service: { bg: '#9E9E9E', text: '#fff' },
  en_maintenance: { bg: '#FF9800', text: '#fff' },
  en_panne: { bg: '#f44336', text: '#fff' },
};

const PlanningManagement: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [weekData, setWeekData] = useState<WeekPlanningResponse | null>(null);
  const [monthEntries, setMonthEntries] = useState<PlanningEntry[]>([]);
  const [yearEntries, setYearEntries] = useState<PlanningEntry[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState<PlanningEntry | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<{ date: string; start_time: string; end_time: string } | null>(null);
  const [selectedAtelierTab, setSelectedAtelierTab] = useState<number>(0); // 0 = Global, 1+ = specific atelier
  const [selectedMachineTab, setSelectedMachineTab] = useState<number>(0); // 0 = Toutes, 1+ = specific machine of the selected atelier
  const [atelierMachines, setAtelierMachines] = useState<MachineDropdownItem[]>([]);

  // "Imprimer le planning d'une machine sur une période" dialog state
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printStartDate, setPrintStartDate] = useState('');
  const [printEndDate, setPrintEndDate] = useState('');
  const [printGenerating, setPrintGenerating] = useState(false);

  // Dropdown data
  const [projets, setProjets] = useState<Projet[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [ateliers, setAteliers] = useState<{ id: number; code: string; name: string }[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [machines, setMachines] = useState<MachineDropdownItem[]>([]);
  const [machineConflicts, setMachineConflicts] = useState<MachineConflictEntry[]>([]);

  // Form state
  const [formData, setFormData] = useState<{
    type: 'projet' | 'service';
    projet_id: number | null;
    service_id: number | null;
    client_id: number | null;
    atelier_id: number | null;
    machine_id: number | null;
    responsable_ids: number[];
    date: string;
    start_time: string;
    end_time: string;
    status: PlanningStatus;
    notes: string;
  }>({
    type: 'projet',
    projet_id: null,
    service_id: null,
    client_id: null,
    atelier_id: null,
    machine_id: null,
    responsable_ids: [],
    date: '',
    start_time: '08:00',
    end_time: '09:00',
    status: 'en_cours',
    notes: '',
  });
  // Bureau d'études (V8): no new planning entry before the plan of the projet / service is validated
  const { data: beEtat } = useBEEtat(
    formData.type === 'projet' || formData.type === 'service' ? formData.type : null,
    formData.type === 'projet' ? formData.projet_id : formData.service_id,
  );

  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [projetsRes, servicesRes, ateliersRes, usersRes] = await Promise.all([
          // the /all endpoints: the paginated lists stopped at the 100 newest
          projetsAPI.getAllForDropdown(),
          servicesAPI.getAllForDropdown(),
          ateliersAPI.getAllForDropdown(),
          userAPI.getUsers(),
        ]);
        const newest = (a: { created_at: string }, b: { created_at: string }) => b.created_at.localeCompare(a.created_at);
        setProjets([...(projetsRes || [])].sort(newest));
        setServices([...(servicesRes || [])].sort(newest));
        setAteliers(ateliersRes || []);
        setUsers(usersRes || []);
      } catch (err) {
        console.error('Error fetching dropdown data:', err);
      }
    };
    fetchDropdownData();
  }, []);

  // Fetch data based on view mode
  const fetchPlanningData = useCallback(async () => {
    try {
      setLoading(true);

      if (viewMode === 'week') {
        const dateStr = currentDate.toISOString().split('T')[0];
        const data = await planningAPI.getWeek(dateStr);
        setWeekData(data);
      } else if (viewMode === 'month') {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const startDate = new Date(year, month, 1).toISOString().split('T')[0];
        const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
        const data = await planningAPI.getAll(0, 1000, startDate, endDate);
        setMonthEntries(data.items || []);
      } else if (viewMode === 'year') {
        const year = currentDate.getFullYear();
        const startDate = `${year}-01-01`;
        const endDate = `${year}-12-31`;
        const data = await planningAPI.getAll(0, 5000, startDate, endDate);
        setYearEntries(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching planning data:', err);
      showError('Erreur lors du chargement du planning');
    } finally {
      setLoading(false);
    }
  }, [currentDate, viewMode]);

  useEffect(() => {
    fetchPlanningData();
  }, [fetchPlanningData]);

  // Load machines for the active atelier tab so we can render a sub-row of
  // machine filter tabs underneath. Reset machine tab whenever atelier changes.
  useEffect(() => {
    setSelectedMachineTab(0);
    if (selectedAtelierTab === 0) {
      setAtelierMachines([]);
      return;
    }
    const atelierId = ateliers[selectedAtelierTab - 1]?.id;
    if (!atelierId) {
      setAtelierMachines([]);
      return;
    }
    let cancelled = false;
    machineAPI
      .getByAtelier(atelierId, true)
      .then((data) => {
        if (!cancelled) setAtelierMachines(data);
      })
      .catch(() => {
        if (!cancelled) setAtelierMachines([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAtelierTab, ateliers]);

  // Live machine-conflict check: runs whenever machine/date/time changes in the
  // open dialog so the user is warned inline (under the Machine field) instead
  // of only seeing the conflict after clicking "Créer".
  useEffect(() => {
    if (!openDialog) {
      setMachineConflicts([]);
      return;
    }
    const { machine_id, date, start_time, end_time } = formData;
    if (!machine_id || !date || !start_time || !end_time) {
      setMachineConflicts([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      planningAPI
        .checkMachineConflict({
          machine_id,
          date,
          start_time: start_time + ':00',
          end_time: end_time + ':00',
          exclude_planning_id: editingEntry ? editingEntry.id : undefined,
        })
        .then((resp) => {
          if (!cancelled) {
            setMachineConflicts(resp.has_conflict ? resp.conflicting_entries : []);
          }
        })
        .catch(() => {
          if (!cancelled) setMachineConflicts([]);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [
    openDialog,
    formData.machine_id,
    formData.date,
    formData.start_time,
    formData.end_time,
    editingEntry,
  ]);

  // Load machines whenever the dialog's atelier changes (or the dialog opens).
  // Always include unavailable machines so users see the real status (en service,
  // hors service, en maintenance, en panne) via the colored chip in the dropdown.
  useEffect(() => {
    if (!openDialog || !formData.atelier_id) {
      setMachines([]);
      return;
    }
    let cancelled = false;
    machineAPI
      .getByAtelier(formData.atelier_id, true)
      .then((data) => {
        if (!cancelled) setMachines(data);
      })
      .catch(() => {
        if (!cancelled) setMachines([]);
      });
    return () => {
      cancelled = true;
    };
  }, [openDialog, formData.atelier_id]);

  // Navigation functions
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setFullYear(newDate.getFullYear() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else {
      newDate.setFullYear(newDate.getFullYear() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get week dates
  const getWeekDates = (): Date[] => {
    if (!weekData) return [];
    const dates: Date[] = [];
    const start = new Date(weekData.week_start);
    for (let i = 0; i < 6; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  // Get month calendar data
  const getMonthCalendarData = (): (Date | null)[][] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Adjust for Monday start (0 = Monday, 6 = Sunday)
    let startDayOfWeek = firstDay.getDay() - 1;
    if (startDayOfWeek < 0) startDayOfWeek = 6;

    const weeks: (Date | null)[][] = [];
    let currentWeek: (Date | null)[] = [];

    // Fill empty days at the start
    for (let i = 0; i < startDayOfWeek; i++) {
      currentWeek.push(null);
    }

    // Fill actual days
    for (let day = 1; day <= lastDay.getDate(); day++) {
      currentWeek.push(new Date(year, month, day));
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    // Fill empty days at the end
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }

    return weeks;
  };

  // Get selected atelier ID (0 = Global/all, otherwise specific atelier)
  const getSelectedAtelierId = (): number | null => {
    if (selectedAtelierTab === 0) return null; // Global - show all
    return ateliers[selectedAtelierTab - 1]?.id || null;
  };

  // Get selected machine ID for the sub-row (0 = Toutes, 1+ = specific machine).
  // Only meaningful when an atelier tab is selected.
  const getSelectedMachineId = (): number | null => {
    if (selectedAtelierTab === 0 || selectedMachineTab === 0) return null;
    return atelierMachines[selectedMachineTab - 1]?.id || null;
  };

  // The machine object backing the active machine sub-tab (null = "Toutes").
  const getSelectedMachine = (): MachineDropdownItem | null => {
    if (selectedAtelierTab === 0 || selectedMachineTab === 0) return null;
    return atelierMachines[selectedMachineTab - 1] || null;
  };

  // Open the "print machine planning over a range" dialog, pre-filling the range
  // from the currently visible view (week/month) — or today as a sane fallback.
  const handleOpenPrintDialog = () => {
    const toISO = (d: Date) => {
      const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
      return tz.toISOString().split('T')[0];
    };
    let start: string;
    let end: string;
    if (viewMode === 'week' && weekData) {
      start = weekData.week_start.substring(0, 10);
      end = weekData.week_end.substring(0, 10);
    } else if (viewMode === 'month') {
      const y = currentDate.getFullYear();
      const m = currentDate.getMonth();
      start = toISO(new Date(y, m, 1));
      end = toISO(new Date(y, m + 1, 0));
    } else if (viewMode === 'year') {
      const y = currentDate.getFullYear();
      start = `${y}-01-01`;
      end = `${y}-12-31`;
    } else {
      start = toISO(new Date());
      end = start;
    }
    setPrintStartDate(start);
    setPrintEndDate(end);
    setPrintDialogOpen(true);
  };

  // Fetch the selected machine's entries over the chosen range and render the
  // schedule-sheet PDF in the shared preview (Print/Download live in its toolbar).
  const handleGenerateMachinePlanningPDF = async () => {
    const machine = getSelectedMachine();
    const atelierId = getSelectedAtelierId();
    if (!machine || atelierId === null) {
      showError('Sélectionnez une machine avant d\'imprimer son planning.');
      return;
    }
    if (!printStartDate || !printEndDate) {
      showError('Veuillez renseigner une date de début et une date de fin.');
      return;
    }
    if (printStartDate > printEndDate) {
      showError('La date de début doit être antérieure ou égale à la date de fin.');
      return;
    }

    setPrintGenerating(true);
    try {
      const res = await planningAPI.getAll(0, 1000, printStartDate, printEndDate, atelierId);
      const entries = (res.items || []).filter((e) => e.machine_id === machine.id);
      const atelierName = ateliers[selectedAtelierTab - 1]?.name;

      setPrintDialogOpen(false);
      openPreview({
        getBlob: async () => {
          const { generateMachinePlanningPDF } = await import('../../../utils/machinePlanningPdfGenerator');
          return generateMachinePlanningPDF(
            {
              machineCode: machine.code,
              machineName: machine.name,
              atelierName,
              startDate: printStartDate,
              endDate: printEndDate,
              entries,
            },
            true,
          ) as Blob;
        },
        fileName: `planning_machine_${machine.code}_${printStartDate}_${printEndDate}.pdf`,
      });
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setPrintGenerating(false);
    }
  };

  // Filter entries by selected atelier (and machine, if a machine tab is active).
  const filterByAtelier = (entries: PlanningEntry[]): PlanningEntry[] => {
    const atelierId = getSelectedAtelierId();
    const machineId = getSelectedMachineId();
    let result = entries;
    if (atelierId !== null) {
      result = result.filter(entry => entry.atelier_id === atelierId);
    }
    if (machineId !== null) {
      result = result.filter(entry => entry.machine_id === machineId);
    }
    return result;
  };

  // Per-view project → color assignment. Walks the palette starting at each
  // code's preferred (hash-derived) slot and skips slots already taken by an
  // earlier-sorted code visible in the same view, so two distinct codes won't
  // share a color while ≤ palette-size codes are visible. Stable for a given
  // set of codes — only changes when the visible set changes (e.g. switching
  // weeks, changing the atelier filter).
  const projectColorMap = useMemo(() => {
    let entries: PlanningEntry[] = [];
    if (viewMode === 'week') entries = weekData?.entries || [];
    else if (viewMode === 'month') entries = monthEntries;
    // year view shows aggregate counts, no per-project chips
    const visible = filterByAtelier(entries);
    const codes = new Set<string>();
    for (const e of visible) {
      const c = e.projet_code || e.service_code;
      if (c) codes.add(c);
    }

    const sorted = Array.from(codes).sort();
    const used = new Set<number>();
    const map: Record<string, string> = {};
    for (const code of sorted) {
      let h = 2166136261;
      for (let i = 0; i < code.length; i++) {
        h ^= code.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      let idx = (h >>> 0) % PROJECT_PALETTE.length;
      let attempts = 0;
      while (used.has(idx) && attempts < PROJECT_PALETTE.length) {
        idx = (idx + 1) % PROJECT_PALETTE.length;
        attempts++;
      }
      used.add(idx);
      map[code] = PROJECT_PALETTE[idx];
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, weekData, monthEntries, selectedAtelierTab, selectedMachineTab]);

  // Get entries for a specific date
  const getEntriesForDate = (date: Date): PlanningEntry[] => {
    const dateStr = date.toISOString().split('T')[0];
    let entries: PlanningEntry[] = [];
    if (viewMode === 'week' && weekData) {
      entries = weekData.entries.filter(entry => entry.date === dateStr);
    } else if (viewMode === 'month') {
      entries = monthEntries.filter(entry => entry.date === dateStr);
    }
    return filterByAtelier(entries);
  };

  // Per-day merged blocks for the week view. Consecutive slots that share
  // (project|service code, machine_id, status) collapse into a single block.
  // Each block also gets a `lane` (vertical row index): blocks are placed in
  // the lowest lane that doesn't already overlap an earlier block, so chips
  // never visually collide even when one starts mid-day.
  type Block = { slotIdx: number; span: number; entry: PlanningEntry; lane: number };
  type DayLayout = { blocks: Block[]; numLanes: number };

  const mergedBlocksByDate = useMemo(() => {
    const map = new Map<string, DayLayout>();
    if (!weekData) return map;
    // String-compare HH:MM directly to keep ordering correct for non-hour-aligned
    // start times (e.g., "08:30") that wouldn't appear in TIME_SLOTS.
    const startKey = (e: PlanningEntry) => e.start_time.substring(0, 5);
    const filtered = filterByAtelier(weekData.entries);

    const byDate = new Map<string, PlanningEntry[]>();
    for (const e of filtered) {
      const list = byDate.get(e.date) || [];
      list.push(e);
      byDate.set(e.date, list);
    }

    byDate.forEach((dayEntries, date) => {
      const groupOrder: string[] = [];
      const groups = new Map<string, PlanningEntry[]>();
      for (const e of dayEntries) {
        const key = `${e.projet_code || e.service_code || ''}|${e.machine_id ?? 'none'}|${e.status}`;
        if (!groups.has(key)) {
          groups.set(key, []);
          groupOrder.push(key);
        }
        groups.get(key)!.push(e);
      }

      const raw: { slotIdx: number; span: number; entry: PlanningEntry }[] = [];
      const totalSlots = TIME_SLOTS.length - 1; // 24
      for (const key of groupOrder) {
        const gEntries = groups.get(key)!;
        const sorted = [...gEntries].sort((a, b) => startKey(a).localeCompare(startKey(b)));
        const cover = new Map<number, PlanningEntry>();
        // Slot-coverage predicate matches the original getEntriesForSlot logic:
        // a slot at HH:00 is covered when its start time falls inside the
        // entry's [start_time, end_time) range (with overnight wrap). This
        // correctly handles non-hour-aligned end times like "09:18" — the
        // entry covers slots 08:00 and 09:00 only.
        for (const e of sorted) {
          const entryStart = e.start_time.substring(0, 5);
          const entryEnd = e.end_time.substring(0, 5);
          if (entryStart === entryEnd) continue; // zero-duration: skip
          const overnight = entryEnd < entryStart;
          for (let i = 0; i < totalSlots; i++) {
            const slotTime = TIME_SLOTS[i];
            const inRange = overnight
              ? slotTime >= entryStart || slotTime < entryEnd
              : slotTime >= entryStart && slotTime < entryEnd;
            if (inRange && !cover.has(i)) cover.set(i, e);
          }
        }
        const slots = Array.from(cover.keys()).sort((a, b) => a - b);
        let runStart: number | null = null;
        let runEnd: number | null = null;
        const flush = () => {
          if (runStart === null || runEnd === null) return;
          raw.push({
            slotIdx: runStart,
            span: runEnd - runStart + 1,
            entry: cover.get(runStart)!,
          });
        };
        for (const idx of slots) {
          if (runStart === null || runEnd === null) {
            runStart = idx;
            runEnd = idx;
          } else if (idx === runEnd + 1) {
            runEnd = idx;
          } else {
            flush();
            runStart = idx;
            runEnd = idx;
          }
        }
        flush();
      }

      // Lane assignment: greedy interval scheduling. Sort by start slot, then
      // place each block in the lowest lane whose last-seen end <= block.start.
      raw.sort((a, b) => a.slotIdx - b.slotIdx);
      const laneEnd: number[] = [];
      const blocks: Block[] = [];
      for (const b of raw) {
        let lane = laneEnd.findIndex((end) => end <= b.slotIdx);
        if (lane === -1) {
          lane = laneEnd.length;
          laneEnd.push(0);
        }
        laneEnd[lane] = b.slotIdx + b.span;
        blocks.push({ ...b, lane });
      }
      map.set(date, { blocks, numLanes: Math.max(1, laneEnd.length) });
    });

    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekData, selectedAtelierTab, selectedMachineTab]);

  // Get entries for a specific month (year view)
  const getEntriesForMonth = (monthIndex: number): PlanningEntry[] => {
    const year = currentDate.getFullYear();
    const entries = yearEntries.filter(entry => {
      const entryDate = new Date(entry.date);
      return entryDate.getFullYear() === year && entryDate.getMonth() === monthIndex;
    });
    return filterByAtelier(entries);
  };

  // Open dialog for new entry
  const handleOpenNewDialog = (date: Date, startTime?: string) => {
    const dateStr = date.toISOString().split('T')[0];
    const endTimeIndex = startTime ? TIME_SLOTS.indexOf(startTime) + 1 : 1;
    const endTime = TIME_SLOTS[endTimeIndex] || '09:00';

    // Pre-select atelier if viewing a specific atelier tab, and pre-select
    // machine if a specific machine sub-tab is also active.
    const preSelectedAtelierId = getSelectedAtelierId();
    const preSelectedMachineId = getSelectedMachineId();

    setSelectedSlot({ date: dateStr, start_time: startTime || '08:00', end_time: endTime });
    setEditingEntry(null);
    setFormData({
      type: 'projet',
      projet_id: null,
      service_id: null,
      client_id: null,
      atelier_id: preSelectedAtelierId,
      machine_id: preSelectedMachineId,
      responsable_ids: [],
      date: dateStr,
      start_time: startTime || '08:00',
      end_time: endTime,
      status: 'en_cours',
      notes: '',
    });
    setOpenDialog(true);
  };

  // Open dialog from the global "+ Nouvelle planification" button.
  // No atelier is pre-selected — user must pick one inside the dialog.
  const handleOpenGlobalNewDialog = () => {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    setSelectedSlot(null);
    setEditingEntry(null);
    setFormData({
      type: 'projet',
      projet_id: null,
      service_id: null,
      client_id: null,
      atelier_id: null,
      machine_id: null,
      responsable_ids: [],
      date: dateStr,
      start_time: '08:00',
      end_time: '09:00',
      status: 'en_cours',
      notes: '',
    });
    setOpenDialog(true);
  };

  // Open dialog for editing entry
  const handleOpenEditDialog = (entry: PlanningEntry) => {
    setEditingEntry(entry);
    setSelectedSlot(null);
    setFormData({
      type: entry.projet_id ? 'projet' : 'service',
      projet_id: entry.projet_id || null,
      service_id: entry.service_id || null,
      client_id: entry.client_id || null,
      atelier_id: entry.atelier_id,
      machine_id: entry.machine_id ?? null,
      responsable_ids: (entry.responsables || []).map(r => r.id),
      date: entry.date,
      start_time: entry.start_time.substring(0, 5),
      end_time: entry.end_time.substring(0, 5),
      status: entry.status,
      notes: entry.notes || '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEntry(null);
    setSelectedSlot(null);
  };

  const persistEntry = async (data: PlanningCreate, force: boolean): Promise<void> => {
    const isEdit = !!editingEntry;
    const saved = isEdit
      ? await planningAPI.update(editingEntry!.id, data as PlanningUpdate, force)
      : await planningAPI.create(data, force);

    handleCloseDialog();
    fetchPlanningData();

    showConfirm(
      `Planification ${isEdit ? 'mise à jour' : 'créée'} avec succès. Voulez-vous afficher le PDF ?`,
      () => {
        openPreview({
          getBlob: async () => {
            const { generatePlanningPDF } = await import('../../../utils/planningPdfGenerator');
            return generatePlanningPDF(saved, true) as Blob;
          },
          fileName: `planification_${saved.id}_${saved.date}.pdf`,
        });
      },
      {
        title: isEdit ? 'Planification mise à jour' : 'Planification créée',
        confirmText: 'Aperçu PDF',
        cancelText: 'Plus tard',
      },
    );
  };

  const handleSave = async () => {
    // Validation
    if (!formData.atelier_id) {
      showError('Veuillez sélectionner un atelier');
      return;
    }
    if (!formData.machine_id) {
      showError('Veuillez sélectionner une machine');
      return;
    }
    if (formData.responsable_ids.length === 0) {
      showError('Veuillez sélectionner au moins un responsable');
      return;
    }
    if (formData.type === 'projet' && !formData.projet_id) {
      showError('Veuillez sélectionner un projet');
      return;
    }
    if (formData.type === 'service' && !formData.service_id) {
      showError('Veuillez sélectionner un service');
      return;
    }

    try {
      setLoading(true);

      // Get client_id from projet or service
      const selectedProjet = formData.type === 'projet' ? projets.find(p => p.id === formData.projet_id) : null;
      const selectedService = formData.type === 'service' ? services.find(s => s.id === formData.service_id) : null;
      const client_id = selectedProjet?.client_id || selectedService?.client_id || undefined;

      const data: PlanningCreate = {
        projet_id: formData.type === 'projet' ? formData.projet_id! : undefined,
        service_id: formData.type === 'service' ? formData.service_id! : undefined,
        client_id: client_id,
        atelier_id: formData.atelier_id!,
        machine_id: formData.machine_id!,
        responsable_ids: formData.responsable_ids,
        date: formData.date,
        start_time: formData.start_time + ':00',
        end_time: formData.end_time + ':00',
        status: formData.status,
        notes: formData.notes || undefined,
      };

      // Conflicts are surfaced inline under the Machine field via the live
      // check effect. If the user clicks Create with conflicts visible, treat
      // it as an explicit override and force-save.
      await persistEntry(data, machineConflicts.length > 0);
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (entry: PlanningEntry) => {
    showConfirm(
      `Êtes-vous sûr de vouloir supprimer cette planification ?`,
      async () => {
        try {
          setLoading(true);
          await planningAPI.delete(entry.id);
          showSuccess('Planification supprimée avec succès');
          fetchPlanningData();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const formatDateHeader = (date: Date, dayIndex: number): string => {
    const day = date.getDate();
    const month = date.toLocaleDateString('fr-FR', { month: 'short' });
    return `${DAYS_FR[dayIndex]} ${day} ${month}`;
  };

  const getNavigationLabel = (): string => {
    if (viewMode === 'week' && weekData) {
      return `${new Date(weekData.week_start).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} - ${new Date(weekData.week_end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`;
    } else if (viewMode === 'month') {
      return `${MONTHS_FR[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    } else {
      return `${currentDate.getFullYear()}`;
    }
  };

  const weekDates = getWeekDates();
  const monthCalendarData = getMonthCalendarData();

  // Render entry chip/card
  // Pixel width of a single time-slot cell in the week view. Used by the CSS
  // Grid template columns and the lane layout below.
  const SLOT_WIDTH_PX = 85;

  const renderEntryChip = (entry: PlanningEntry, compact: boolean = false) => (
    <Tooltip
      key={entry.id}
      title={
        <Box>
          <Typography variant="body2">
            {entry.projet_code ? `Projet: ${entry.projet_code}` : `Service: ${entry.service_code}`}
          </Typography>
          <Typography variant="body2">
            {entry.projet_nom || entry.service_name}
          </Typography>
          {entry.client_name && (
            <Typography variant="body2">
              Client: {entry.client_code} - {entry.client_name}
            </Typography>
          )}
          <Typography variant="body2">
            Atelier: {entry.atelier_name}
          </Typography>
          {entry.machine_code && (
            <Typography variant="body2">
              Machine: {entry.machine_code} — {entry.machine_name}
            </Typography>
          )}
          <Typography variant="body2">
            {(entry.responsables?.length ?? 0) > 1 ? 'Responsables' : 'Responsable'}:{' '}
            {(entry.responsables || []).map(r => r.full_name).filter(Boolean).join(' · ') || '—'}
          </Typography>
          <Typography variant="body2">
            {entry.start_time.substring(0, 5)} - {entry.end_time.substring(0, 5)}
          </Typography>
          {entry.notes && (
            <Typography variant="body2">Notes: {entry.notes}</Typography>
          )}
        </Box>
      }
      arrow
    >
      <Box
        sx={{
          bgcolor: projectColorMap[entry.projet_code || entry.service_code || ''] || projectColor(entry.projet_code || entry.service_code),
          color: '#fff',
          // Inset left bar carries the status color (en_cours/termine/bloque)
          // without breaking the rounded corners.
          boxShadow: `inset 4px 0 0 0 ${STATUS_COLORS[entry.status].bg}`,
          pl: compact ? '6px' : '8px',
          pr: compact ? 0.25 : 0.5,
          py: compact ? 0.25 : 0.5,
          borderRadius: 1,
          mb: 0.5,
          fontSize: compact ? '0.65rem' : '0.75rem',
          cursor: 'pointer',
          position: 'relative',
          '&:hover .entry-actions': {
            opacity: 1,
          },
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', fontSize: compact ? '0.6rem' : '0.75rem' }}>
          {entry.projet_code || entry.service_code}
          {entry.machine_code ? ` (M-${entry.machine_code})` : ''}
        </Typography>
        {!compact && (
          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem' }}>
            {entry.atelier_name}
          </Typography>
        )}
        <Box
          className="entry-actions"
          sx={{
            position: 'absolute',
            top: 2,
            right: 2,
            opacity: 0,
            transition: 'opacity 0.2s',
            display: 'flex',
            gap: 0.5,
          }}
        >
          <IconButton
            size="small"
            sx={{ p: 0.25, color: 'rgba(0,0,0,0.7)', bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { color: 'rgba(0,0,0,0.87)', bgcolor: 'white' } }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditDialog(entry);
            }}
          >
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton
            size="small"
            sx={{ p: 0.25, color: 'rgba(0,0,0,0.7)', bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { color: 'rgba(0,0,0,0.87)', bgcolor: 'white' } }}
            onClick={(e) => {
              e.stopPropagation();
              openPreview({
                getBlob: async () => {
                  const { generatePlanningPDF } = await import('../../../utils/planningPdfGenerator');
                  return generatePlanningPDF(entry, true) as Blob;
                },
                fileName: `planification_${entry.id}_${entry.date}.pdf`,
              });
            }}
          >
            <PrintIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton
            size="small"
            sx={{ p: 0.25, color: 'rgba(0,0,0,0.7)', bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { color: 'rgba(0,0,0,0.87)', bgcolor: 'white' } }}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(entry);
            }}
          >
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    </Tooltip>
  );

  // Render Week View
  const renderWeekView = () => (
    <Box
      sx={{
        overflowX: 'auto',
        '&::-webkit-scrollbar': { height: 10 },
        '&::-webkit-scrollbar-track': { bgcolor: 'action.hover' },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'action.disabled', borderRadius: 5 },
      }}
    >
      <Box sx={{ width: 'fit-content' }}>
        {/* Header Row - Time slots */}
        <Grid container sx={{ borderBottom: (theme) => `2px solid ${theme.palette.divider}`, flexWrap: 'nowrap' }}>
          <Grid item sx={{ width: 120, minWidth: 120, p: 1, bgcolor: 'action.hover', borderRight: (theme) => `1px solid ${theme.palette.divider}` }}>
            <Typography variant="subtitle2" fontWeight="bold">Jour</Typography>
          </Grid>
          {TIME_SLOTS.slice(0, -1).map((timeSlot, index) => (
            <Grid
              item
              key={timeSlot}
              sx={{
                width: 85,
                minWidth: 85,
                p: 0.5,
                bgcolor: 'action.hover',
                borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
                {timeSlot}-{TIME_SLOTS[index + 1]}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Day Rows — CSS Grid: 1 column for the date label + 24 slot columns.
            Slot cells span all rows for the background grid (border + click);
            chips occupy specific (column-range, lane-row) cells on top. */}
        {weekDates.map((date, dayIndex) => {
          const dateStr = date.toISOString().split('T')[0];
          const layout = mergedBlocksByDate.get(dateStr) || { blocks: [], numLanes: 1 };
          const isGlobalView = selectedAtelierTab === 0;
          return (
            <Box
              key={dayIndex}
              sx={{
                display: 'grid',
                gridTemplateColumns: `120px repeat(24, ${SLOT_WIDTH_PX}px)`,
                gridAutoRows: 'minmax(34px, auto)',
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                minHeight: 70,
              }}
            >
              <Box
                sx={{
                  gridColumn: '1',
                  gridRow: '1 / -1',
                  p: 1,
                  bgcolor: 'action.hover',
                  borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.75rem' }}>
                  {formatDateHeader(date, dayIndex)}
                </Typography>
              </Box>
              {/* Background slot cells — provide vertical separators, hover, and
                  click-to-create. They span every lane row so the grid stays
                  visible behind chips. */}
              {TIME_SLOTS.slice(0, -1).map((timeSlot, timeIndex) => (
                <Box
                  key={timeSlot}
                  sx={{
                    gridColumn: `${timeIndex + 2}`,
                    gridRow: '1 / -1',
                    borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                    cursor: isGlobalView ? 'default' : 'pointer',
                    '&:hover': isGlobalView ? {} : {
                      bgcolor: 'rgba(33, 150, 243, 0.05)',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={() => !isGlobalView && handleOpenNewDialog(date, timeSlot)}
                >
                  {!isGlobalView && layout.blocks.every(b => timeIndex < b.slotIdx || timeIndex >= b.slotIdx + b.span) && (
                    <AddIcon sx={{ color: '#bdbdbd', fontSize: 20 }} />
                  )}
                </Box>
              ))}
              {/* Chips — placed by lane (row) and slot range (column span). */}
              {layout.blocks.map((b, i) => (
                <Box
                  key={`${b.entry.id}-${i}`}
                  sx={{
                    gridColumn: `${b.slotIdx + 2} / span ${b.span}`,
                    gridRow: `${b.lane + 1}`,
                    px: '2px',
                    minWidth: 0,
                  }}
                >
                  {renderEntryChip(b.entry)}
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  // Render Month View
  const renderMonthView = () => (
    <Box>
      {/* Days header */}
      <Grid container sx={{ borderBottom: (theme) => `2px solid ${theme.palette.divider}` }}>
        {DAYS_FR_SHORT.map((day, index) => (
          <Grid
            item
            key={day}
            sx={{
              flex: 1,
              p: 1,
              bgcolor: 'action.hover',
              borderRight: index < 6 ? (theme) => `1px solid ${theme.palette.divider}` : 'none',
              textAlign: 'center',
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold">{day}</Typography>
          </Grid>
        ))}
      </Grid>

      {/* Calendar weeks */}
      {monthCalendarData.map((week, weekIndex) => {
        const isGlobalView = selectedAtelierTab === 0;
        return (
        <Grid container key={weekIndex} sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}>
          {week.map((date, dayIndex) => {
            const entries = date ? getEntriesForDate(date) : [];
            const isToday = date && date.toDateString() === new Date().toDateString();
            const canClick = date && !isGlobalView;
            return (
              <Grid
                item
                key={dayIndex}
                sx={{
                  flex: 1,
                  minHeight: 100,
                  p: 0.5,
                  borderRight: dayIndex < 6 ? (theme) => `1px solid ${theme.palette.divider}` : 'none',
                  bgcolor: date ? (isToday ? 'rgba(255, 87, 34, 0.1)' : 'background.paper') : 'action.hover',
                  cursor: canClick ? 'pointer' : 'default',
                  '&:hover': canClick ? {
                    bgcolor: isToday ? 'rgba(255, 87, 34, 0.15)' : 'rgba(33, 150, 243, 0.05)',
                  } : {},
                }}
                onClick={() => canClick && handleOpenNewDialog(date)}
              >
                {date && (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isToday ? 'bold' : 'normal',
                        color: isToday ? '#FF5722' : 'text.primary',
                        mb: 0.5,
                      }}
                    >
                      {date.getDate()}
                    </Typography>
                    <Box sx={{ maxHeight: 70, overflow: 'auto' }}>
                      {entries.slice(0, 3).map((entry) => renderEntryChip(entry, true))}
                      {entries.length > 3 && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          +{entries.length - 3} autres
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </Grid>
            );
          })}
        </Grid>
        );
      })}
    </Box>
  );

  // Render Year View
  const renderYearView = () => (
    <Grid container spacing={2}>
      {MONTHS_FR.map((month, monthIndex) => {
        const entries = getEntriesForMonth(monthIndex);
        const entriesByStatus = {
          en_cours: entries.filter(e => e.status === 'en_cours').length,
          termine: entries.filter(e => e.status === 'termine').length,
          bloque: entries.filter(e => e.status === 'bloque').length,
        };

        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={month}>
            <Paper
              sx={{
                p: 2,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'rgba(255, 87, 34, 0.05)',
                  boxShadow: 3,
                },
                transition: 'all 0.2s',
              }}
              onClick={() => {
                const newDate = new Date(currentDate.getFullYear(), monthIndex, 1);
                setCurrentDate(newDate);
                setViewMode('month');
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ color: '#FF5722' }}>
                {month}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {entries.length} planification{entries.length !== 1 ? 's' : ''}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {entriesByStatus.en_cours > 0 && (
                  <Chip
                    label={`${entriesByStatus.en_cours} en cours`}
                    size="small"
                    sx={{ bgcolor: STATUS_COLORS.en_cours.bg, color: STATUS_COLORS.en_cours.text, mb: 0.5 }}
                  />
                )}
                {entriesByStatus.termine > 0 && (
                  <Chip
                    label={`${entriesByStatus.termine} terminé${entriesByStatus.termine !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{ bgcolor: STATUS_COLORS.termine.bg, color: STATUS_COLORS.termine.text, mb: 0.5 }}
                  />
                )}
                {entriesByStatus.bloque > 0 && (
                  <Chip
                    label={`${entriesByStatus.bloque} bloqué${entriesByStatus.bloque !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{ bgcolor: STATUS_COLORS.bloque.bg, color: STATUS_COLORS.bloque.text, mb: 0.5 }}
                  />
                )}
              </Stack>

              {/* Mini list of recent entries */}
              {entries.length > 0 && (
                <Box sx={{ mt: 1, maxHeight: 100, overflow: 'auto' }}>
                  {entries.slice(0, 3).map((entry) => (
                    <Box
                      key={entry.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 0.5,
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEditDialog(entry);
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: STATUS_COLORS[entry.status].bg,
                        }}
                      />
                      <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                        {entry.projet_code || entry.service_code} - {new Date(entry.date).getDate()}/{monthIndex + 1}
                      </Typography>
                    </Box>
                  ))}
                  {entries.length > 3 && (
                    <Typography variant="caption" color="text.secondary">
                      +{entries.length - 3} autres
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h5">
            Gestion du Planning
          </Typography>

          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newValue) => newValue && setViewMode(newValue)}
            size="small"
          >
            <ToggleButton value="week">
              <ViewWeekIcon sx={{ mr: 0.5 }} /> Semaine
            </ToggleButton>
            <ToggleButton value="month">
              <ViewMonthIcon sx={{ mr: 0.5 }} /> Mois
            </ToggleButton>
            <ToggleButton value="year">
              <ViewYearIcon sx={{ mr: 0.5 }} /> Année
            </ToggleButton>
          </ToggleButtonGroup>

          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton onClick={goToPrevious} title={viewMode === 'week' ? 'Semaine précédente' : viewMode === 'month' ? 'Mois précédent' : 'Année précédente'}>
              <ChevronLeftIcon />
            </IconButton>
            <Button
              variant="outlined"
              startIcon={<TodayIcon />}
              onClick={goToToday}
            >
              Aujourd'hui
            </Button>
            <IconButton onClick={goToNext} title={viewMode === 'week' ? 'Semaine suivante' : viewMode === 'month' ? 'Mois suivant' : 'Année suivante'}>
              <ChevronRightIcon />
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', minWidth: 250, textAlign: 'center' }}>
              {getNavigationLabel()}
            </Typography>
          </Stack>
        </Stack>

        {/* Legend */}
        <Stack direction="row" spacing={2} mb={2}>
          {Object.entries(STATUS_COLORS).map(([status, colors]) => (
            <Chip
              key={status}
              label={colors.label}
              size="small"
              sx={{ bgcolor: colors.bg, color: colors.text }}
            />
          ))}
        </Stack>

        {/* Atelier Tabs + global "Nouvelle planification" button on the right */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: 1,
            borderColor: 'divider',
            mb: 2,
          }}
        >
          <Tabs
            value={selectedAtelierTab}
            onChange={(_, newValue) => setSelectedAtelierTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              flex: 1,
              minWidth: 0,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
              },
              '& .Mui-selected': {
                color: '#FF5722',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#FF5722',
              },
            }}
          >
            <Tab label="Global" />
            {ateliers.map((atelier) => (
              <Tab key={atelier.id} label={atelier.name} />
            ))}
          </Tabs>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenGlobalNewDialog}
            sx={{ bgcolor: '#FF5722', '&:hover': { bgcolor: '#E64A19' }, flexShrink: 0, mb: 1 }}
          >
            Nouvelle planification
          </Button>
        </Box>

        {/* Machine sub-tabs — only when a specific atelier is selected */}
        {selectedAtelierTab !== 0 && atelierMachines.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderBottom: 1,
              borderColor: 'divider',
              mb: 2,
            }}
          >
            <Tabs
              value={selectedMachineTab}
              onChange={(_, newValue) => setSelectedMachineTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 36,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  minHeight: 36,
                  py: 0.5,
                },
                '& .Mui-selected': {
                  color: '#4CAF50',
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#4CAF50',
                },
              }}
            >
              <Tab label="Toutes" />
              {atelierMachines.map((m) => {
                const dot = MACHINE_STATUS_COLORS[m.status]?.bg || '#9E9E9E';
                return (
                  <Tab
                    key={m.id}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: dot,
                            flexShrink: 0,
                          }}
                        />
                        <span>{m.code} — {m.name}</span>
                      </Box>
                    }
                  />
                );
              })}
            </Tabs>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              disabled={selectedMachineTab === 0}
              onClick={handleOpenPrintDialog}
              title={
                selectedMachineTab === 0
                  ? 'Sélectionnez une machine pour imprimer son planning'
                  : 'Imprimer le planning de cette machine sur une période'
              }
              sx={{
                flexShrink: 0,
                textTransform: 'none',
                color: '#4CAF50',
                borderColor: '#4CAF50',
                '&:hover': { borderColor: '#388E3C', bgcolor: 'rgba(76,175,80,0.08)' },
              }}
            >
              Imprimer planning
            </Button>
          </Box>
        )}

        {/* Calendar View */}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'month' && renderMonthView()}
        {viewMode === 'year' && renderYearView()}
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEntry ? 'Modifier la planification' : 'Nouvelle planification'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Type Selection */}
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={formData.type}
                  onChange={(e) => setFormData({
                    ...formData,
                    type: e.target.value as 'projet' | 'service',
                    projet_id: null,
                    service_id: null,
                    client_id: null,
                  })}
                >
                  <FormControlLabel value="projet" control={<Radio />} label="Projet" />
                  <FormControlLabel value="service" control={<Radio />} label="Service" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Projet or Service Selection */}
            <Grid item xs={12}>
              {formData.type === 'projet' ? (
                <Autocomplete
                  options={projets}
                  getOptionLabel={(option) => `${option.code} - ${option.nom}`}
                  value={projets.find(p => p.id === formData.projet_id) || null}
                  onChange={(_, value) => setFormData({
                    ...formData,
                    projet_id: value?.id || null,
                    client_id: value?.client_id || null // Auto-fill client from projet
                  })}
                  renderInput={(params) => (
                    <TextField {...params} label="Projet" variant="filled" required />
                  )}
                />
              ) : (
                <Autocomplete
                  options={services}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  value={services.find(s => s.id === formData.service_id) || null}
                  onChange={(_, value) => setFormData({
                    ...formData,
                    service_id: value?.id || null,
                    client_id: value?.client_id || null // Auto-fill client from service
                  })}
                  renderInput={(params) => (
                    <TextField {...params} label="Service" variant="filled" required />
                  )}
                />
              )}
            </Grid>

            {beEtat?.avertissement && (
              <Grid item xs={12}>
                <Alert severity={editingEntry ? 'warning' : 'error'}>
                  {beEtat.avertissement.split(' : ')[0]}.{' '}
                  {editingEntry
                    ? 'La date et l\'heure ne peuvent plus changer avant la validation du plan.'
                    : 'Planification impossible avant la validation du plan par le bureau d\'études.'}
                </Alert>
              </Grid>
            )}

            {/* Client Display (read-only - from projet or service) */}
            {formData.type === 'projet' && formData.projet_id && (
              <Grid item xs={12}>
                {(() => {
                  const selectedProjet = projets.find(p => p.id === formData.projet_id);
                  return (
                    <TextField
                      label="Client"
                      value={selectedProjet?.client_name ? `${selectedProjet.client_name}` : 'Aucun client associé'}
                      variant="filled"
                      fullWidth
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: selectedProjet?.client_name ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.38)',
                        },
                      }}
                    />
                  );
                })()}
              </Grid>
            )}

            {/* Client Display (read-only - from service) */}
            {formData.type === 'service' && formData.service_id && (
              <Grid item xs={12}>
                {(() => {
                  const selectedService = services.find(s => s.id === formData.service_id);
                  return (
                    <TextField
                      label="Client"
                      value={selectedService?.client_name ? `${selectedService.client_name}` : 'Aucun client associé'}
                      variant="filled"
                      fullWidth
                      disabled
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: selectedService?.client_name ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.38)',
                        },
                      }}
                    />
                  );
                })()}
              </Grid>
            )}

            {/* Atelier Selection */}
            <Grid item xs={12}>
              <Autocomplete
                options={ateliers}
                getOptionLabel={(option) => option.name}
                value={ateliers.find(a => a.id === formData.atelier_id) || null}
                onChange={(_, value) => setFormData({
                  ...formData,
                  atelier_id: value?.id || null,
                  machine_id: null,
                })}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField {...params} label="Atelier *" variant="filled" required />
                )}
              />
            </Grid>

            {/* Machine Selection */}
            <Grid item xs={12}>
              <Autocomplete
                options={machines}
                getOptionLabel={(option) => `${option.code} — ${option.name}`}
                value={machines.find((m) => m.id === formData.machine_id) || null}
                onChange={(_, value) => setFormData({ ...formData, machine_id: value?.id || null })}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                disabled={!formData.atelier_id}
                noOptionsText={
                  !formData.atelier_id
                    ? 'Sélectionnez un atelier d\'abord'
                    : 'Aucune machine disponible pour cet atelier'
                }
                renderOption={(props, option) => {
                  const colors = MACHINE_STATUS_COLORS[option.status];
                  return (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {option.code} — {option.name}
                      </Typography>
                      <Chip
                        label={MACHINE_STATUS_LABELS[option.status]}
                        size="small"
                        sx={{
                          bgcolor: colors.bg,
                          color: colors.text,
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          height: 22,
                        }}
                      />
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Machine *" variant="filled" required />
                )}
              />
              {machineConflicts.length > 0 && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1.25,
                    borderRadius: 1,
                    bgcolor: 'rgba(244, 67, 54, 0.08)',
                    border: '1px solid rgba(244, 67, 54, 0.4)',
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#c62828', fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Cette machine est déjà utilisée sur ce créneau :
                  </Typography>
                  {machineConflicts.map((c) => {
                    const code = c.projet_code || c.service_code || `#${c.id}`;
                    const start = (c.start_time || '').substring(0, 5);
                    const end = (c.end_time || '').substring(0, 5);
                    const respNames = (c.responsables || []).map(r => r.full_name).filter(Boolean).join(' · ');
                    const resp = respNames ? ` — ${respNames}` : '';
                    return (
                      <Typography
                        key={c.id}
                        variant="caption"
                        sx={{ color: '#c62828', display: 'block', lineHeight: 1.6 }}
                      >
                        • {code} ({start}–{end}){resp}
                      </Typography>
                    );
                  })}
                </Box>
              )}
            </Grid>

            {/* Responsables Selection (multi) */}
            <Grid item xs={12}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={users}
                getOptionLabel={(option) => option.full_name || `${option.first_name} ${option.last_name}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={users.filter(u => formData.responsable_ids.includes(u.id))}
                onChange={(_, value) =>
                  setFormData({ ...formData, responsable_ids: value.map(v => v.id) })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={formData.responsable_ids.length > 1 ? 'Responsables' : 'Responsable'}
                    variant="filled"
                    required
                  />
                )}
              />
            </Grid>

            {/* Date */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                fullWidth
                variant="filled"
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Start Time */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Heure début"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                fullWidth
                variant="filled"
                required
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
              />
            </Grid>

            {/* End Time */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Heure fin"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                fullWidth
                variant="filled"
                required
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                fullWidth
                variant="filled"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading || !formData.machine_id || (!editingEntry && !!beEtat?.avertissement)}
            sx={{ bgcolor: '#FF5722', '&:hover': { bgcolor: '#E64A19' } }}
          >
            {editingEntry ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      {/* Imprimer le planning d'une machine sur une période */}
      <Dialog open={printDialogOpen} onClose={() => setPrintDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Imprimer le planning machine
          {getSelectedMachine() && (
            <Typography variant="body2" color="text.secondary">
              {getSelectedMachine()!.code} — {getSelectedMachine()!.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Sélectionnez la période à imprimer. La fiche regroupe les tâches par jour
              et peut être affichée près de la machine pour l'opérateur.
            </Typography>
            <TextField
              label="Date de début"
              type="date"
              value={printStartDate}
              onChange={(e) => setPrintStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Date de fin"
              type="date"
              value={printEndDate}
              onChange={(e) => setPrintEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  const tz = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
                  const today = tz.toISOString().split('T')[0];
                  setPrintStartDate(today);
                  setPrintEndDate(today);
                }}
                sx={{ textTransform: 'none' }}
              >
                Aujourd'hui
              </Button>
              {weekData && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    setPrintStartDate(weekData.week_start.substring(0, 10));
                    setPrintEndDate(weekData.week_end.substring(0, 10));
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Semaine en cours
                </Button>
              )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintDialogOpen(false)} disabled={printGenerating}>
            Annuler
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handleGenerateMachinePlanningPDF}
            disabled={printGenerating}
            sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#388E3C' } }}
          >
            {printGenerating ? 'Génération…' : 'Aperçu PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default PlanningManagement;
